<template>
  <div id="appointments" class="appointments-list">
    <header class="appointments-list__header">
      <div class="appointments-list__headline">
        <h1 class="appointments-list__title">Agendamentos</h1>
        <p class="appointments-list__subtitle">
          Veja os seus compromissos futuros e passados.
        </p>
      </div>
      <Button
        label="Novo agendamento"
        icon="pi pi-plus"
        class="appointments-list__cta p-button-sm"
        @click="$router.push({ name: 'appointments-new' })"
      />
    </header>

    <div class="appointments-list__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        class="list-tab"
        :class="{ 'list-tab--active': activeTab === tab.value }"
        @click="activeTab = tab.value"
      >
        {{ tab.label }}
      </button>
    </div>

    <ProgressBar
      v-if="isLoading"
      mode="indeterminate"
      class="appointments-list__loading mb-3"
    />

    <div
      v-else-if="filteredAppointments.length === 0"
      class="appointments-list__empty"
    >
      <i class="pi pi-calendar" />
      <p>
        Sem agendamentos
        {{
          activeTab === "future"
            ? "futuros"
            : activeTab === "past"
            ? "passados"
            : ""
        }}.
      </p>
      <Button
        label="Agendar agora"
        icon="pi pi-plus"
        class="p-button-outlined mt-2"
        @click="$router.push({ name: 'appointments-new' })"
      />
    </div>

    <div v-else class="appointments-list__grid">
      <AppointmentCard
        v-for="appointment in filteredAppointments"
        :key="appointment.id || appointment.appointmentId"
        :appointment="appointment"
        @click="openEditAppointment(appointment)"
      />
    </div>

    <Dialog
      v-model:visible="editDialog.visible"
      header="Editar agendamento"
      :modal="true"
      :draggable="false"
      class="appointments-edit-dialog"
      :style="{ width: 'min(960px, 96vw)' }"
      @hide="resetEditDialog"
    >
      <div v-if="editDialog.loading" class="appointments-edit-dialog__loading">
        <ProgressBar mode="indeterminate" />
        <p class="mt-2 mb-0">A preparar edição...</p>
      </div>

      <div
        v-else-if="editDialog.appointment && editDialog.service"
        class="appointments-edit-dialog__content"
      >
        <div class="appointments-edit-dialog__summary">
          <h4 class="appointments-edit-dialog__title">Resumo atual</h4>
          <div class="appointments-edit-dialog__summary-grid">
            <div>
              <span>Serviço</span>
              <strong>{{
                editDialog.appointment.servicePriceName ||
                editDialog.service.name ||
                "-"
              }}</strong>
            </div>
            <div>
              <span>Data / Hora</span>
              <strong>{{
                formatDateTime(editDialog.appointment.startDate)
              }}</strong>
            </div>
            <div>
              <span>Consultor</span>
              <strong>{{
                editDialog.appointment.consultantName || "-"
              }}</strong>
            </div>
            <div>
              <span>Tipo de agendamento</span>
              <strong>{{
                formatType(
                  editDialog.appointment.appointmentType ||
                    editDialog.appointment.type
                )
              }}</strong>
            </div>
          </div>
        </div>

        <AppointmentStepDateTime
          v-model="editDialog.dateTime"
          :service="editDialog.service"
          :appointment-mode="editDialog.mode"
          :office-id="editOfficeIdForAvailability"
          :office-options="editOfficeOptionsForUi"
          :block-presencial-without-office="editBlockPresencialNoOffices"
          :needs-presencial-office-choice="editNeedsPresencialOfficeChoice"
          :office-gate-message="editPresencialOfficeGateMessage"
          @valid="editDialog.stepValid = $event"
          @office-change="onEditPresencialOfficeChange"
        />
      </div>

      <template #footer>
        <Button
          label="Cancelar"
          class="p-button-outlined"
          :disabled="editDialog.saving"
          @click="editDialog.visible = false"
        />
        <Button
          label="Guardar nova data"
          icon="pi pi-check"
          :disabled="!editDialog.stepValid || editDialog.saving"
          :loading="editDialog.saving"
          @click="submitEditAppointment"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import Dialog from "primevue/dialog";
import AppointmentCard from "./AppointmentCard.vue";
import AppointmentStepDateTime from "./AppointmentStepDateTime.vue";

export default {
  name: "AppointmentsList",
  components: { AppointmentCard, AppointmentStepDateTime, Dialog },
  data() {
    return {
      isLoading: false,
      appointmentsList: [],
      activeTab: "future",
      editDialog: {
        visible: false,
        loading: false,
        saving: false,
        stepValid: false,
        appointment: null,
        service: null,
        mode: null,
        dateTime: null,
        presencialOfficeId: null,
      },
      tabs: [
        { label: "Agendamentos Futuros", value: "future" },
        { label: "Passados", value: "past" },
        { label: "Todos", value: "all" },
      ],
    };
  },
  computed: {
    filteredAppointments() {
      const now = dayjs();
      return this.appointmentsList
        .filter((a) => {
          const start = a.startDate ? dayjs.utc(a.startDate).local() : null;
          if (!start) return this.activeTab === "all";
          if (this.activeTab === "future") return start.isAfter(now);
          if (this.activeTab === "past") return start.isBefore(now);
          return true;
        })
        .sort((a, b) => {
          const da = a.startDate ? new Date(a.startDate) : 0;
          const db = b.startDate ? new Date(b.startDate) : 0;
          return this.activeTab === "past" ? db - da : da - db;
        });
    },
    editOfficeIdForAvailability() {
      if (this.editDialog.mode !== "on-site") return null;
      return this.editDialog.presencialOfficeId != null
        ? Number(this.editDialog.presencialOfficeId)
        : null;
    },
    editPresencialOfficesAllowed() {
      const s = this.editDialog.service;
      if (!s || !Array.isArray(s.offices)) return [];
      const countries = this.clientCountriesForFilter();
      return s.offices.filter(function (o) {
        return !o.country || countries.indexOf(o.country) !== -1;
      });
    },
    editOfficeOptionsForUi() {
      return this.editPresencialOfficesAllowed.map(function (o) {
        var city = o.city || "";
        var country = o.country || "";
        var label = city
          ? city + (country ? " (" + country + ")" : "")
          : String(o.id);
        return { id: Number(o.id), label: label };
      });
    },
    editBlockPresencialNoOffices() {
      if (this.editDialog.mode !== "on-site") return false;
      return this.editPresencialOfficesAllowed.length === 0;
    },
    editNeedsPresencialOfficeChoice() {
      if (this.editDialog.mode !== "on-site") return false;
      var allowed = this.editPresencialOfficesAllowed;
      return allowed.length > 1 && this.editDialog.presencialOfficeId == null;
    },
    editPresencialOfficeGateMessage() {
      if (this.editDialog.mode !== "on-site") return "";
      var allowed = this.editPresencialOfficesAllowed;
      if (allowed.length === 0) {
        return "Não há balcões presenciais com agenda para este serviço na sua região. Pode optar por videoconferência (se disponível) ou contactar-nos.";
      }
      if (allowed.length > 1 && this.editDialog.presencialOfficeId == null) {
        return "Indique em que balcão quer ser atendido.";
      }
      return "";
    },
  },
  created() {
    this.fetchList();
  },
  methods: {
    clientCountriesForFilter() {
      const country = this.$root.user && this.$root.user.country;
      if (!country || country === "PT") return ["PT"];
      return [country, "PT"];
    },
    formatType(rawType) {
      if (rawType === "online") return "Online";
      if (rawType === "on-site") return "Presencial";
      if (rawType === "hybrid") return "Online ou presencial";
      return rawType || "-";
    },
    formatDateTime(startDate) {
      if (!startDate) return "-";
      return dayjs.utc(startDate).local().format("DD/MM/YYYY HH:mm");
    },
    isPastAppointment(appointment) {
      if (!appointment || !appointment.startDate) return false;
      return dayjs.utc(appointment.startDate).local().isBefore(dayjs());
    },
    ensureEditPresencialOfficeSelection() {
      if (this.editDialog.mode !== "on-site") return;
      var allowed = this.editPresencialOfficesAllowed;
      if (allowed.length === 0) {
        this.editDialog.presencialOfficeId = null;
        return;
      }
      var current =
        this.editDialog.presencialOfficeId != null
          ? Number(this.editDialog.presencialOfficeId)
          : null;
      if (
        current != null &&
        allowed.some(function (o) {
          return Number(o.id) === current;
        })
      )
        return;
      if (allowed.length === 1) {
        this.editDialog.presencialOfficeId = Number(allowed[0].id);
        return;
      }
      this.editDialog.presencialOfficeId = null;
    },
    onEditPresencialOfficeChange(officeId) {
      this.editDialog.presencialOfficeId =
        officeId != null ? Number(officeId) : null;
      this.editDialog.dateTime = null;
      this.editDialog.stepValid = false;
    },
    resetEditDialog() {
      this.editDialog.visible = false;
      this.editDialog.loading = false;
      this.editDialog.saving = false;
      this.editDialog.stepValid = false;
      this.editDialog.appointment = null;
      this.editDialog.service = null;
      this.editDialog.mode = null;
      this.editDialog.dateTime = null;
      this.editDialog.presencialOfficeId = null;
    },
    async openEditAppointment(appointment) {
      const id = Number(
        appointment && (appointment.id || appointment.appointmentId)
      );
      if (!id) return;
      if (this.isPastAppointment(appointment)) {
        this.$root.toastError("appointment_past_not_editable");
        return;
      }
      this.editDialog.visible = true;
      this.editDialog.loading = true;
      this.editDialog.appointment = appointment;
      try {
        const servicePriceId = Number(appointment.servicePriceId);
        const serviceRes = await this.$root.apiGet(
          this.$root.api.servicePrices,
          {
            params: {
              id: servicePriceId,
              acceptAppointments: true,
              serviceInClientArea: true,
              isActive: true,
              state: "active",
            },
          }
        );
        const services =
          serviceRes && serviceRes.data && Array.isArray(serviceRes.data.data)
            ? serviceRes.data.data
            : [];
        const selectedService = services.find(
          (s) => Number(s.id) === servicePriceId
        );
        if (!selectedService) {
          this.$root.toastError("service_not_found");
          this.editDialog.visible = false;
          return;
        }
        this.editDialog.service = selectedService;
        this.editDialog.mode =
          appointment.appointmentType ||
          appointment.type ||
          selectedService.meetingType;
        this.editDialog.presencialOfficeId =
          appointment.officeId != null ? Number(appointment.officeId) : null;
        this.ensureEditPresencialOfficeSelection();
        const localStart = appointment.startDate
          ? dayjs.utc(appointment.startDate).local()
          : null;
        this.editDialog.dateTime = localStart
          ? {
              date: localStart.format("YYYY-MM-DD"),
              time: localStart.format("HH:mm"),
              startDateUTC: `${localStart.format(
                "YYYY-MM-DD"
              )} ${localStart.format("HH:mm")}`,
            }
          : null;
        this.editDialog.stepValid = !!this.editDialog.dateTime;
      } catch (error) {
        this.$root.toastError(
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.msg_code) ||
            "error"
        );
        this.editDialog.visible = false;
      } finally {
        this.editDialog.loading = false;
      }
    },
    async submitEditAppointment() {
      if (
        !this.editDialog.stepValid ||
        !this.editDialog.appointment ||
        !this.editDialog.dateTime
      )
        return;
      this.editDialog.saving = true;
      const appointment = this.editDialog.appointment;
      const id = Number(appointment.id || appointment.appointmentId);
      let officeId = null;
      if (
        (appointment.appointmentType || appointment.type) === "on-site" &&
        this.editDialog.presencialOfficeId != null
      ) {
        officeId = Number(this.editDialog.presencialOfficeId);
      }
      try {
        await this.$root.apiPut(
          `${this.$root.api.appointmentsReschedule}/${id}/reschedule`,
          {
            startDate: this.editDialog.dateTime.startDateUTC,
            officeId,
          }
        );
        this.$root.toastSuccess("appointment_updated");
        this.editDialog.visible = false;
        this.fetchList();
      } catch (error) {
        this.$root.toastError(
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.msg_code) ||
            "error"
        );
      } finally {
        this.editDialog.saving = false;
      }
    },
    fetchList() {
      this.isLoading = true;
      this.$root
        .apiGet(this.$root.api.appointments, { params: { dataType: "list" } })
        .then((response) => {
          this.appointmentsList = response.data.data || [];
        })
        .catch((error) => {
          this.$root.toastError(
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "error"
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
